<template>
  <main class="bg-gray-100 min-h-screen">
    <header class="max-w-4xl">
      <div
        class="border-b border-gray-100 px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8"
      >
        <div class="flex-1 min-w-0">
          <h1 class="text-lg font-medium leading-6 text-gray-900 sm:truncate">
            {{ $t('patient_add_title') }}
          </h1>
        </div>
      </div>
      <sqr-progress v-show="loading" />
      <sqr-error-banner :error="loadError" @retry="patientInit(true)" />
    </header>

    <section v-if="patient && !loadError && !loading">
      <div class="py-8 max-w-4xl lg:px-8">
        <patient-form
          :patient="patient"
          @change="fieldSet($event)"
          :validations="$v.patient"
        />
        <div
          class="sticky bottom-0 border-t border-gray-300 bg-gray-100 mt-8 p-4 -mx-1"
        >
          <sqr-error-text :error="saveError" />
          <div class="flex justify-end space-x-2">
            <sqr-button
              color="transparent"
              :label="$t('buttons_cancel')"
              @click="$router.back()"
              size="md"
            />
            <sqr-button
              :label="$t('patient_add_confirm')"
              @click="saveAndGo()"
              :is-loading="saving"
              color="primary"
              size="md"
            >
              <template slot="icon">
                <CheckIcon class="inline w-4 h-4 stroke-current" />
              </template>
            </sqr-button>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { validationMixin } from 'vuelidate';
import SqrButton from '@/components/SqrButton';
import SqrProgress from '@/components/SqrProgress';
import SqrErrorBanner from '@/components/SqrErrorBanner';
import SqrErrorText from '@/components/SqrErrorText';
import sideUpdate from '../utils/sideUpdate';
import PatientForm from '../forms/PatientForm';
import patientVal from '../validations/patient';
import patientName from '../filters/patient';
import { CheckIcon } from '@vue-hero-icons/outline';

export default {
  name: 'PatientAdd',
  mixins: [validationMixin],
  components: {
    PatientForm,
    SqrButton,
    SqrProgress,
    SqrErrorBanner,
    SqrErrorText,
    CheckIcon
  },
  props: {
    aid: String
  },
  computed: {
    ...mapGetters('account', ['account', 'loading', 'loadError'])
  },
  data() {
    return {
      patient: {
        givenName: '',
        familyName: '',
        gender: '',
        canton: '',
        law: '',
        refundType: ''
      },
      saving: false,
      saveError: null
    };
  },
  validations: {
    patient: patientVal
  },
  async mounted() {
    this.$a().logEvent('patient_add');
    this.accountDefaults();
  },
  methods: {
    ...mapActions('account', { accountLoad: 'load' }),
    async accountDefaults() {
      const account = await this.accountLoad({});
      this.$set(this.patient, 'canton', account.canton);
      this.$set(this.patient, 'law', account.law);
      this.$set(this.patient, 'refundType', account.refundType);
    },
    fieldSet({ field, value }) {
      this.$set(this.patient, field, value);
    },
    async saveAndGo() {
      try {
        this.saving = true;
        this.saveError = null;
        if (this.$v.$invalid) {
          this.$v.$touch();
          this.$nextTick(() => alert(this.$t('patient_validation')));
          return;
        }
        const aid = this.aid;
        const path = `accounts/${aid}/patients`;
        const col = this.$db().collection(path);
        const patient = this.patient;
        patient.name = patientName(this.patient);
        const res = await col.add(patient);
        const patientId = res.id;
        await sideUpdate(this.$db(), this.$route.query, aid, {
          patientId,
          patient
        });
        this.$router.back();
      } catch (error) {
        this.saveError = error;
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
